import { useNotifications } from '@/data/Content/Notifications';
import { useGetInterests } from 'integration/hooks/useGetInterests';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { useUserInterests } from 'integration/hooks/useUserInterests';
import valueIsNullOrUndef from 'integration/utils/typeAssertions/valueIsNullOrUndef';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const HOBBIES_CODE = 'hobbies';

export const useHobbies = () => {
	const { showSnackbarMessage, notifyError } = useNotifications();
	const [submitting, setSubmitting] = useState(false);
	const [selectedUserHobbies, setSelectedUserHobbies] = useState<number[]>([]);
	const isHobbiesInitialized = useRef(false);
	const openHobbiesModal = useIsOpen();

	const hobbiesListQuery = useGetInterests();

	const userHobbiesHook = useUserInterests();

	const userHobbiesData = useMemo(
		() =>
			userHobbiesHook.userInterests?.find(
				(item) => item.name.toLocaleLowerCase().trim() === HOBBIES_CODE
			),
		[userHobbiesHook.userInterests]
	);

	const hobbiesListData = useMemo(
		() =>
			hobbiesListQuery.data?.find((item) => item.name.toLocaleLowerCase().trim() === HOBBIES_CODE),
		[hobbiesListQuery.data]
	);

	const noneId = useMemo(
		() =>
			hobbiesListData?.children.find((c) => c.name.trim().toLowerCase().includes('ninguno'))?.id,
		[hobbiesListData]
	);

	useEffect(() => {
		const userHobbies = userHobbiesData?.children || [];
		if (isHobbiesInitialized.current || userHobbies.length === 0) return;
		const hobbiesIds = userHobbies.map((hobby) => hobby.id);
		setSelectedUserHobbies(hobbiesIds);
		isHobbiesInitialized.current = true;
	}, [userHobbiesData]);

	const onUpdateUserHobbies = useCallback(async () => {
		const parentId = hobbiesListData?.id;

		if (valueIsNullOrUndef(parentId)) {
			notifyError('Por favor intente nuevamente más tarde', 'Ocurrió un problema');
			return;
		}

		setSubmitting(true);
		try {
			await userHobbiesHook.onUpdateUserInterests([{ parentId, children: selectedUserHobbies }]);
			showSnackbarMessage('Hobbies actualizados correctamente');
		} catch {
			notifyError('Por favor intente nuevamente más tarde', 'Ocurrió un problema');
		} finally {
			setSubmitting(false);
			openHobbiesModal.off();
		}
	}, [
		showSnackbarMessage,
		notifyError,
		hobbiesListData?.id,
		openHobbiesModal,
		selectedUserHobbies,
		userHobbiesHook,
	]);

	const onToggleUserHobbies = useCallback(
		(interestId: number) => {
			setSelectedUserHobbies((prev) => {
				if (interestId === noneId) {
					return prev.includes(interestId) ? [] : [interestId];
				}
				const withoutNinguno = noneId !== undefined ? prev.filter((id) => id !== noneId) : prev;
				return withoutNinguno.includes(interestId)
					? withoutNinguno.filter((id) => id !== interestId)
					: [...withoutNinguno, interestId];
			});
		},
		[noneId]
	);

	return {
		hobbies: hobbiesListData?.children || [],
		userHobbies: userHobbiesData?.children || [],
		isSubmittingHobbies: submitting,
		isLoadingHobbies: hobbiesListQuery.isLoading,
		selectedUserHobbies,
		openHobbiesModal: openHobbiesModal.open,
		onOpenHobbiesModal: openHobbiesModal.on,
		onCloseHobbiesModal: openHobbiesModal.off,
		onUpdateUserHobbies,
		onToggleUserHobbies,
	};
};
